import React, { useState } from "react";
import AnnualRevenueChart from "./Annualchart";
import { Line } from "react-chartjs-2";
import GraficButton from "./GraficButton";
import { dashboardTheme } from "../../theme/dashboardTheme";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface IRentabilityGraficProps {
  data: { date: string; final_value: number }[];
  chartZoom: number | "all";
  onChartZoomChange: (zoom: number | "all") => void;
}

const ZOOM_PERIODS = [12, 24, 36] as const;

const RentabilityGrafic: React.FC<IRentabilityGraficProps> = ({
  data,
  chartZoom,
  onChartZoomChange,
}) => {
  const [showChart, setShowChart] = useState(true);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDateDisplay = (dateString: string) => {
    const [day, month, year] = dateString.split("/");
    const date = new Date(`${year}-${month}-${day}`);
    return isNaN(date.getTime()) ? "Data inválida" :
      `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
  };

  const sortedData = [...data].sort((a, b) => {
    const [dayA, monthA, yearA] = a.date.split("/");
    const [dayB, monthB, yearB] = b.date.split("/");
    return new Date(`${yearA}-${monthA}-${dayA}`).getTime() - new Date(`${yearB}-${monthB}-${dayB}`).getTime();
  });

  const chartData = {
    labels: sortedData.map((item) => formatDateDisplay(item.date)),
    datasets: [
      {
        data: sortedData.map((item) => item.final_value),
        borderColor: dashboardTheme.colors.chartLine,
        backgroundColor: (context: { chart: { ctx: CanvasRenderingContext2D; chartArea?: { top: number; bottom: number } } }) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return dashboardTheme.colors.chartFill;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(110, 168, 255, 0.35)");
          gradient.addColorStop(1, "rgba(110, 168, 255, 0.02)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: dashboardTheme.colors.chartLine,
        pointHoverBorderColor: "#f0f6fc",
        pointHoverBorderWidth: 2,
        borderWidth: 2.5,
        label: "Variação mensal do seu imóvel",
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales: {
      y: {
        border: { display: false },
        grid: {
          color: dashboardTheme.colors.chartGrid,
          drawTicks: false,
        },
        ticks: {
          color: dashboardTheme.colors.chartTick,
          padding: 8,
          callback: (value: string | number) => formatCurrency(Number(value)),
        },
      },
      x: {
        border: { display: false },
        grid: {
          display: false,
        },
        ticks: {
          color: dashboardTheme.colors.chartTick,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: dashboardTheme.colors.cardBackground,
        titleColor: dashboardTheme.colors.textPrimary,
        bodyColor: dashboardTheme.colors.textSecondary,
        borderColor: dashboardTheme.colors.cardBorder,
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context: TooltipItem<"line">) => {
            const value = context.parsed.y ?? 0;
            return formatCurrency(value);
          },
        },
      },
    },
  };

  const handlePeriodChange = (period: number | "all") => {
    onChartZoomChange(period);
  };

  return (
    <div className="w-full max-w-full">
      <div className="h-[clamp(220px,38vh,320px)] w-full overflow-x-auto rounded-2xl border border-white/[0.06] bg-[#0d1117]/50 px-2 py-3">
        {showChart ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <AnnualRevenueChart data={sortedData} />
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {showChart && (
          <>
            <button
              type="button"
              onClick={() => handlePeriodChange("all")}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                chartZoom === "all"
                  ? "border-[#6ea8ff]/40 bg-[#6ea8ff]/20 text-[#6ea8ff]"
                  : "border-white/10 bg-transparent text-[#8b949e] hover:bg-white/5"
              }`}
            >
              Tudo
            </button>
            {ZOOM_PERIODS.map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => handlePeriodChange(period)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  chartZoom === period
                    ? "border-[#6ea8ff]/40 bg-[#6ea8ff]/20 text-[#6ea8ff]"
                    : "border-white/10 bg-transparent text-[#8b949e] hover:bg-white/5"
                }`}
              >
                {period}m
              </button>
            ))}
          </>
        )}

        <GraficButton onClick={() => setShowChart(!showChart)}>
          {showChart ? "Receita Anual" : "Receita Mensal"}
        </GraficButton>
      </div>
    </div>
  );
};

export default RentabilityGrafic;